import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Loader2, Scissors, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";

const LAYOUTS = [
  ["blur", "Fondo borroso", "Se ve todo el video, con barras borrosas arriba y abajo"],
  ["crop", "Pantalla completa", "Llena la pantalla, pero corta los laterales"],
  ["band", "Franja del medio", "Video arriba y franja del color del medio abajo"],
];

const MODES = [
  ["auto", "Transcribir y revisar"],
  ["auto-directo", "Transcribir y generar"],
  ["manual", "Los escribo yo"],
  ["none", "Sin subtítulos"],
];

export const ReelDialog = ({ video, onClose, onDone }) => {
  const { data: outlets } = useSWR("/outlets", fetcher);
  const [layout, setLayout] = useState("blur");
  const [mode, setMode] = useState("auto");
  const [start, setStart] = useState(0);
  const [duration, setDuration] = useState(60);
  const [headline, setHeadline] = useState("");
  const [badge, setBadge] = useState("VIDEO");
  const [withLogo, setWithLogo] = useState(true);
  const [outletId, setOutletId] = useState("");
  const [text, setText] = useState("");
  const [segments, setSegments] = useState([]);
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const toText = (segs) => segs.map((s) =>
    `${s.start.toFixed(1)} | ${s.end.toFixed(1)} | ${s.text}`).join("\n");

  const fromText = (value) => value.split("\n").map((line) => {
    const [a, b, ...rest] = line.split("|");
    const inicio = parseFloat(a), fin = parseFloat(b);
    const texto = rest.join("|").trim();
    if (isNaN(inicio) || isNaN(fin) || !texto) return null;
    return { start: inicio, end: fin, text: texto };
  }).filter(Boolean);

  const transcribe = async () => {
    setBusy("stt");
    try {
      const { data } = await api.post(`/videos/library/${video.id}/transcribe`,
        { start: Number(start), duration: Number(duration) || 60 });
      setSegments(data.segments || []);
      setText(toText(data.segments || []));
      toast.success(`${(data.segments || []).length} línea(s) transcritas: revíselas`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    setBusy("reel");
    setStatus("En cola…");
    try {
      const segs = mode === "none" ? []
        : mode === "auto-directo" ? []
        : (text ? fromText(text) : segments);
      const { data: job } = await api.post(`/videos/library/${video.id}/reel`, {
        layout, start: Number(start), duration: Number(duration) || 0,
        subtitles: mode === "auto-directo" ? "auto" : mode === "auto" && !segs.length ? "auto"
                                                    : mode === "none" ? "none" : "manual",
        segments: segs, headline, badge, with_logo: withLogo,
        outlet_id: outletId || null,
      });
      for (let i = 0; i < 90 && alive.current; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        if (!alive.current) return;
        const { data: st } = await api.get(`/videos/reels/status?job_id=${job.job_id}`);
        setStatus(st.detail || st.state);
        if (st.state === "done") {
          toast.success("Recorte vertical listo en la videoteca");
          onDone?.();
          onClose();
          return;
        }
        if (st.state === "error") {
          toast.error(`No se pudo generar: ${st.detail}`);
          return;
        }
      }
      if (alive.current) {
        toast.error("El recorte está tardando demasiado; revise la videoteca en unos minutos");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      if (alive.current) {
        setBusy("");
        setStatus("");
      }
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="grid-cols-1 max-w-2xl max-h-[88vh] overflow-y-auto"
                    data-testid="reel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />Recorte vertical para redes
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Arma un video 9:16 (1080×1920) con los subtítulos quemados, listo para Instagram,
          TikTok y Reels. Se guarda como un video nuevo en la videoteca.
        </p>

        <div>
          <Label className="text-xs uppercase tracking-wider">Encuadre</Label>
          <div className="grid sm:grid-cols-3 gap-2 mt-2">
            {LAYOUTS.map(([value, title, detail]) => (
              <button key={value} onClick={() => setLayout(value)}
                      className={`text-left p-3 border transition-colors ${
                        layout === value ? "border-primary bg-primary/10"
                                         : "border-border hover:bg-muted/60"}`}
                      data-testid={`reel-layout-${value}`}>
                <span className="text-sm font-medium">{title}</span>
                <span className="block text-[11px] text-muted-foreground mt-1">{detail}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Desde el segundo</Label>
            <Input type="number" min="0" value={start} onChange={(e) => setStart(e.target.value)}
                   className="mt-1.5" data-testid="reel-start" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Duración (seg., máx. 60)</Label>
            <Input type="number" min="1" max="60" value={duration}
                   onChange={(e) => setDuration(e.target.value)} className="mt-1.5"
                   data-testid="reel-duration" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Medio</Label>
            <select value={outletId} onChange={(e) => setOutletId(e.target.value)}
                    className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                    data-testid="reel-outlet">
              <option value="">Sin medio</option>
              {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Subtítulos</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {MODES.map(([value, label]) => (
              <button key={value} onClick={() => setMode(value)}
                      className={`text-xs px-3 py-2 border transition-colors ${
                        mode === value ? "border-primary bg-primary/10 text-foreground"
                                       : "border-border text-muted-foreground hover:bg-muted/60"}`}
                      data-testid={`reel-mode-${value}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {(mode === "auto" || mode === "manual") && (
          <div>
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs uppercase tracking-wider">
                Líneas (inicio | fin | texto)
              </Label>
              {mode === "auto" && (
                <Button size="sm" variant="outline" onClick={transcribe} disabled={busy === "stt"}
                        data-testid="reel-transcribe-btn">
                  {busy === "stt" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                  : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                  {busy === "stt" ? "Transcribiendo…" : "Transcribir el audio"}
                </Button>
              )}
            </div>
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)}
                      className="mt-1.5 mono text-xs"
                      placeholder={"0.0 | 3.2 | Primera línea\n3.3 | 6.0 | Segunda línea"}
                      data-testid="reel-subtitles-text" />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Titular (primeros segundos)</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)}
                   className="mt-1.5" data-testid="reel-headline" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Sello</Label>
            <Input value={badge} onChange={(e) => setBadge(e.target.value)} className="mt-1.5"
                   placeholder="VIDEO, EN VIVO…" data-testid="reel-badge" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch checked={withLogo} onCheckedChange={setWithLogo} data-testid="reel-logo-switch" />
          <span className="text-sm">Logo del medio en la esquina</span>
        </div>

        {status && (
          <p className="text-xs text-muted-foreground" data-testid="reel-status">{status}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="reel-cancel">Cancelar</Button>
          <Button onClick={generate} disabled={!!busy} data-testid="reel-generate-btn">
            {busy === "reel" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {busy === "reel" ? "Generando…" : "Generar recorte"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
